import React, { useMemo, useState } from 'react';
import { MInputTextField, MButton, BufferService, DefaultFunctionURL } from 'src/M_Components';
import './ForgotPasswordForm.css';

/**
 * Sends forgot-password to different endpoints based on realm:
 * - realm="user" -> PUT Identity/forgetpassword with { email }
 * - realm="carrier" -> POST CarrierIdentity/password/forgot with { userName }
 */
export default function ForgotPasswordForm({ onBackToLogin, t, ctx, realm = 'user' }) {
  const [emailOrUserName, setEmailOrUserName] = useState('');

  // Instantiate BufferService with useMemo to avoid creating it inside event handlers.
  // Using a stable instance keeps concerns (baseUrl, route) centralized and avoids hook violations if the
  // BufferService or any dependency internally touches React context.
  const baseUrl = DefaultFunctionURL?.() || process.env.REACT_APP_API_BASE_URL || '';
  const isCarrier = realm === 'carrier';
  const service = useMemo(
    () =>
      new BufferService(
        isCarrier ? 'CarrierIdentity/password/forgot' : 'Identity/forgetpassword',
        baseUrl
      ),
    [isCarrier, baseUrl]
  );

  //============================================================================================================
  // Function to handle forget password submission
  //============================================================================================================
  const handleForgetPasswordSubmit = () => {
    // return early if field is empty
    if (emailOrUserName.trim() === '') {
      ctx.toastMessages('error', t('LabelTryAgain'), '');
      return;
    }

    ctx.setIsLoad(true);

    // Build endpoint + verb + body by realm contract
    // keep the backend contract as documenteded. If unify keys later (ex: Username), adjust here.
    const method = isCarrier ? 'post' : 'put';
    const body = isCarrier ? { userName: emailOrUserName } : { Email: emailOrUserName };

    service[method](body)
      .then((response) => {
        ctx.setIsLoad(false);

        if (response?.name === 'AxiosError') {
          ctx.toastMessages('error', t('LabelTryAgain'), '');
          return;
        }

        ctx.toastMessages('success', '', t('LabelEmailSentSuccessfully'));
        setEmailOrUserName('');

        // After sending the email, go back to the proper login page
        // use hash-based routes to stay consistent with the rest of the app.
        setTimeout(() => {
          window.location.href = isCarrier ? '#/carrier/login' : '#/login';
        }, 800);
      })
      .catch((error) => {
        ctx.setIsLoad(false);
        console.error('[ERROR] Unexpected error:', error);
        ctx.toastMessages('error', t('LabelTryAgain'), '');
      });
  };

  //============================================================================================================
  // Form with input + submit button
  //============================================================================================================
  return (
    <div className="forgot-password-container">
      <div className="forgot-password-inner">
        <div className="forgot-password-form">
          <MInputTextField
            // Label remains the same; you may customize if you want "Username" for carrier
            // in carrier realm we show “Username” to match UX expectations for carrier logins.
            label={isCarrier ? (t('Username') || 'Username') : t('textFieldEmailLabel')}
            type="text"
            onChange={(e) => setEmailOrUserName(e.target.value)}
            value={emailOrUserName}
          />

          <MButton
            cy="submitForgotPassword"
            label={t('SendButtonName')}
            labelColor={'var(--color-white)'}
            backgroundColor={ctx.secondaryColor}
            borderColor={ctx.secondaryColor}
            onClick={handleForgetPasswordSubmit}
            bold
            width={85}
          />

          <div className="back-To-Login" onClick={() => onBackToLogin()}>
            {t('BackToLogin')}
          </div>
        </div>
      </div>
    </div>
  );
}
